import { useState, useRef, useEffect } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.1;

export const useImageViewZoom = (isOpen: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [transform, setTransform] = useState({
    scale: MIN_SCALE,
    x: 0,
    y: 0,
  });

  const [isDragging, setIsDragging] = useState(false);

  const dragStart = useRef({
    mouseX: 0,
    mouseY: 0,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (!isOpen) return;

    let cleanup: (() => void) | null = null;

    const trySetupWheel = () => {
      const container = containerRef.current;
      if (!container) {
        requestAnimationFrame(trySetupWheel);
        return;
      }

      const handleWheel = (e: globalThis.WheelEvent) => {
        e.preventDefault();

        const delta = e.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;

        const rect = container.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        setTransform((prev) => {
          const scale = Math.min(
            MAX_SCALE,
            Math.max(MIN_SCALE, +(prev.scale + delta).toFixed(2)),
          );

          if (scale === prev.scale) {
            return prev;
          }

          const ratio = scale / prev.scale;

          return {
            scale,
            x: mouseX - (mouseX - prev.x) * ratio,
            y: mouseY - (mouseY - prev.y) * ratio,
          };
        });
      };

      container.addEventListener("wheel", handleWheel, { passive: false });

      cleanup = () => {
        container.removeEventListener("wheel", handleWheel);
      };
    };

    requestAnimationFrame(trySetupWheel);

    return () => {
      if (cleanup) cleanup();
    };
  }, [isOpen]);

  const onMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();

    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      x: transform.x,
      y: transform.y,
    };

    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      setTransform((prev) => ({
        ...prev,
        x: dragStart.current.x + e.clientX - dragStart.current.mouseX,
        y: dragStart.current.y + e.clientY - dragStart.current.mouseY,
      }));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return {
    containerRef,
    onMouseDown,
    imageStyle: {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
      transformOrigin: "0 0",
      cursor: isDragging ? "grabbing" : "grab",
      userSelect: "none" as const,
      display: "block",
    },
  };
};
